import React, { useState, useEffect, useCallback } from 'react';

import * as Modal from 'components/modal';
import { useApi } from 'hooks';

const ABSTRACT_MAX = 1500; // characters (decision 09/jun)
const TITLE_MAX = 200;

const STATUS_COLORS = {
    draft: { bg: '#e2e8f0', fg: '#475569' },
    submitted: { bg: '#ffedd5', fg: '#ea580c' },
    preApproved: { bg: '#dbeafe', fg: '#2563eb' },
    promoted: { bg: '#dcfce7', fg: '#16a34a' },
};

const inputStyle = {
    width: '100%',
    padding: '8px 12px',
    border: '1px solid #ccc',
    borderRadius: 4,
    fontSize: '0.95em',
    fontFamily: 'inherit',
};

const labelStyle = {
    display: 'block',
    fontWeight: 'bold',
    marginBottom: 6,
    marginTop: 14,
};

function StatusBadge({ status, t }) {
    const c = STATUS_COLORS[status] || STATUS_COLORS.draft;
    return (
        <span
            style={{
                background: c.bg,
                color: c.fg,
                borderRadius: 999,
                padding: '2px 10px',
                fontSize: 11,
                fontWeight: 600,
            }}
        >
            {t(`CorporateStep5OpenInnovation.status_${status}`)}
        </span>
    );
}

// ---------------------------------------------------------------------------
//  "Ver trilhas" modal — lists the 11 tracks
// ---------------------------------------------------------------------------
function TracksModal({ isOpen, close, tracks, t }) {
    return (
        <Modal.Modal
            isOpen={isOpen}
            close={close}
            title={t('CorporateStep5OpenInnovation.tracksModalTitle')}
        >
            <div style={{ padding: 4 }}>
                {tracks.map((tr) => (
                    <div
                        key={tr.id}
                        style={{
                            border: '1px solid #eee',
                            borderRadius: 8,
                            padding: '10px 12px',
                            marginBottom: 8,
                        }}
                    >
                        <div style={{ fontWeight: 600 }}>
                            {tr.id} · {tr.title}
                        </div>
                        <div style={{ fontSize: '0.85em', color: '#666', marginTop: 4 }}>
                            {tr.description}
                        </div>
                    </div>
                ))}
            </div>
        </Modal.Modal>
    );
}

// ---------------------------------------------------------------------------
//  "Adicionar caso" modal — two tabs (Criar do zero / Importar do Congresso)
// ---------------------------------------------------------------------------
function AddCaseModal({
    isOpen,
    close,
    api,
    companyId,
    ranking,
    companyName,
    tracks,
    editing,
    onSaved,
    onOpenTracks,
    t,
}) {
    const emptyFields = {
        title: '',
        abstract: '',
        institution1: companyName || '',
        partnerInstitutions: '',
        track: '',
    };

    const [tab, setTab] = useState('create');
    const [fields, setFields] = useState(emptyFields);
    const [source, setSource] = useState('manual');
    const [importMeta, setImportMeta] = useState(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    // Import tab state
    const [congresses, setCongresses] = useState([]);
    const [impCongress, setImpCongress] = useState('');
    const [impCaseId, setImpCaseId] = useState('');
    const [searching, setSearching] = useState(false);
    const [preview, setPreview] = useState(null);
    const [searchDone, setSearchDone] = useState(false);

    // (Re)initialize whenever the modal opens or the edited story changes.
    useEffect(() => {
        if (!isOpen) return;
        if (editing) {
            setFields({
                title: editing.title || '',
                abstract: editing.abstract || '',
                institution1: editing.institution1 || companyName || '',
                partnerInstitutions: editing.partnerInstitutions || '',
                track: editing.track || '',
            });
            setSource(editing.source || 'manual');
            setImportMeta(editing.import || null);
            setTab('create');
        } else {
            setFields({ ...emptyFields });
            setSource('manual');
            setImportMeta(null);
            setTab('create');
        }
        setError('');
        setPreview(null);
        setSearchDone(false);
        setImpCaseId('');
    }, [isOpen, editing]);

    useEffect(() => {
        if (!isOpen) return;
        api.get('/congresses')
            .then((res) => {
                if (Array.isArray(res)) {
                    setCongresses(res);
                    if (res.length === 1) setImpCongress(res[0]._id);
                }
            })
            .catch(() => {});
    }, [isOpen]);

    const setField = (name, value) =>
        setFields((f) => ({ ...f, [name]: value }));

    const runSearch = async () => {
        if (!impCongress || !impCaseId.trim()) return;
        setSearching(true);
        setSearchDone(false);
        setPreview(null);
        try {
            const res = await api.post(
                `/companies/${companyId}/ranking-cases/import`,
                { congressId: impCongress, caseId: impCaseId.trim() }
            );
            setPreview(res);
        } catch (err) {
            setPreview({ found: false });
        } finally {
            setSearching(false);
            setSearchDone(true);
        }
    };

    const continueFromImport = () => {
        if (!preview || !preview.found) return;
        setFields((f) => ({ ...f, title: preview.title || f.title }));
        setSource('imported');
        setImportMeta({
            congressId: impCongress,
            caseId: impCaseId.trim(),
            abstractUrl: preview.abstractUrl || null,
        });
        setTab('create');
    };

    const validate = () => {
        if (!fields.title.trim()) return 'titleRequired';
        if (fields.title.length > TITLE_MAX) return 'titleTooLong';
        if (!fields.abstract.trim()) return 'abstractRequired';
        if (fields.abstract.length > ABSTRACT_MAX) return 'abstractTooLong';
        if (!fields.track) return 'trackRequired';
        return null;
    };

    const save = async (status) => {
        const err = validate();
        if (err) {
            setError(t(`CorporateStep5OpenInnovation.error_${err}`));
            return;
        }
        setSaving(true);
        setError('');
        try {
            const payload = {
                title: fields.title.trim(),
                abstract: fields.abstract,
                institution1: fields.institution1.trim(),
                partnerInstitutions: fields.partnerInstitutions,
                track: Number(fields.track),
                status,
                source,
            };
            if (source === 'imported' && importMeta) payload.import = importMeta;

            if (editing) {
                await api.put(
                    `/companies/${companyId}/ranking-cases/${editing._id}`,
                    payload
                );
            } else {
                await api.post(
                    `/companies/${companyId}/ranking-cases?ranking=${ranking}`,
                    { ...payload, ranking }
                );
            }
            onSaved();
        } catch (e) {
            setError(t('CorporateStep5OpenInnovation.error_saveFailed'));
        } finally {
            setSaving(false);
        }
    };

    const tabBtn = (key, label) => (
        <button
            type="button"
            onClick={() => setTab(key)}
            style={{
                padding: '8px 14px',
                background: 'none',
                border: 'none',
                borderBottom: tab === key ? '2px solid #4f46e5' : '2px solid transparent',
                color: tab === key ? '#4f46e5' : '#666',
                fontWeight: tab === key ? 600 : 400,
                cursor: 'pointer',
            }}
        >
            {label}
        </button>
    );

    const abstractOver = fields.abstract.length > ABSTRACT_MAX;
    const titleOver = fields.title.length > TITLE_MAX;

    const guidelines = t('CorporateStep5OpenInnovation.guidelines', {
        returnObjects: true,
    });

    return (
        <Modal.Modal
            isOpen={isOpen}
            close={close}
            title={t('CorporateStep5OpenInnovation.modalTitle')}
        >
            <div style={{ padding: 4 }}>
                {/* Tabs (import tab hidden in edit mode) */}
                {!editing && (
                    <div style={{ display: 'flex', borderBottom: '1px solid #eee', marginBottom: 12 }}>
                        {tabBtn('create', t('CorporateStep5OpenInnovation.tabCreate'))}
                        {tabBtn('import', t('CorporateStep5OpenInnovation.tabImport'))}
                    </div>
                )}

                {tab === 'create' && (
                    <div>
                        <label style={labelStyle}>
                            {t('CorporateStep5OpenInnovation.fieldTitle')}
                        </label>
                        <input
                            type="text"
                            value={fields.title}
                            maxLength={TITLE_MAX}
                            onChange={(e) => setField('title', e.target.value)}
                            placeholder={t('CorporateStep5OpenInnovation.fieldTitlePlaceholder')}
                            style={inputStyle}
                        />
                        <div style={{ fontSize: 11, color: titleOver ? '#dc2626' : '#999', marginTop: 4 }}>
                            {fields.title.length} / {TITLE_MAX}
                        </div>

                        <label style={labelStyle}>
                            {t('CorporateStep5OpenInnovation.fieldInstitution1')}
                        </label>
                        <input
                            type="text"
                            value={fields.institution1}
                            readOnly
                            style={{ ...inputStyle, background: '#f1f5f9', color: '#475569' }}
                        />

                        <label style={labelStyle}>
                            {t('CorporateStep5OpenInnovation.fieldPartners')}
                        </label>
                        <textarea
                            value={fields.partnerInstitutions}
                            onChange={(e) => setField('partnerInstitutions', e.target.value)}
                            placeholder={t('CorporateStep5OpenInnovation.fieldPartnersPlaceholder')}
                            rows={2}
                            style={{ ...inputStyle, resize: 'vertical' }}
                        />

                        <label style={labelStyle}>
                            {t('CorporateStep5OpenInnovation.fieldTrack')}
                            {'  '}
                            <button
                                type="button"
                                onClick={onOpenTracks}
                                style={{ color: '#4f46e5', cursor: 'pointer', fontWeight: 400, fontSize: '0.85em' }}
                            >
                                {t('CorporateStep5OpenInnovation.viewTracks')}
                            </button>
                        </label>
                        <select
                            value={fields.track}
                            onChange={(e) => setField('track', e.target.value)}
                            style={inputStyle}
                        >
                            <option value="">
                                {t('CorporateStep5OpenInnovation.selectTrack')}
                            </option>
                            {tracks.map((tr) => (
                                <option key={tr.id} value={tr.id}>
                                    {tr.id} · {tr.title}
                                </option>
                            ))}
                        </select>

                        <label style={labelStyle}>
                            {t('CorporateStep5OpenInnovation.fieldAbstract')}
                        </label>
                        <textarea
                            value={fields.abstract}
                            onChange={(e) => setField('abstract', e.target.value)}
                            placeholder={t('CorporateStep5OpenInnovation.fieldAbstractPlaceholder')}
                            rows={6}
                            style={{ ...inputStyle, resize: 'vertical' }}
                        />
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                fontSize: 11,
                                marginTop: 4,
                            }}
                        >
                            <span style={{ color: abstractOver ? '#dc2626' : '#16a34a', fontWeight: 500 }}>
                                {fields.abstract.length} / {ABSTRACT_MAX}{' '}
                                {t('CorporateStep5OpenInnovation.charsLabel')}
                            </span>
                        </div>

                        <details style={{ marginTop: 10 }}>
                            <summary style={{ cursor: 'pointer', color: '#4f46e5', fontSize: '0.9em' }}>
                                {t('CorporateStep5OpenInnovation.guidelinesToggle')}
                            </summary>
                            <ul style={{ fontSize: '0.85em', color: '#555', marginTop: 6 }}>
                                {Array.isArray(guidelines) &&
                                    guidelines.map((g, i) => <li key={i}>{g}</li>)}
                            </ul>
                        </details>

                        {error && (
                            <p style={{ color: '#dc2626', fontSize: '0.9em', marginTop: 12 }}>{error}</p>
                        )}

                        <div style={{ display: 'flex', gap: 8, marginTop: 20, justifyContent: 'flex-end' }}>
                            <button
                                type="button"
                                onClick={() => save('draft')}
                                disabled={saving || abstractOver || titleOver}
                                style={{
                                    padding: '8px 16px',
                                    background: '#fff',
                                    color: '#333',
                                    border: '1px solid #ccc',
                                    borderRadius: 6,
                                    cursor: saving ? 'not-allowed' : 'pointer',
                                }}
                            >
                                {t('CorporateStep5OpenInnovation.saveDraft')}
                            </button>
                            <button
                                type="button"
                                onClick={() => save('submitted')}
                                disabled={saving || abstractOver || titleOver}
                                style={{
                                    padding: '8px 16px',
                                    background: saving ? '#cfd5dc' : '#4f46e5',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: 6,
                                    cursor: saving ? 'not-allowed' : 'pointer',
                                }}
                            >
                                {t('CorporateStep5OpenInnovation.submit')}
                            </button>
                        </div>
                    </div>
                )}

                {tab === 'import' && !editing && (
                    <div>
                        <p style={{ fontSize: '0.9em', color: '#555', marginBottom: 8 }}>
                            {t('CorporateStep5OpenInnovation.importIntro')}
                        </p>

                        <label style={labelStyle}>
                            {t('CorporateStep5OpenInnovation.congressLabel')}
                        </label>
                        <select
                            value={impCongress}
                            onChange={(e) => setImpCongress(e.target.value)}
                            style={inputStyle}
                        >
                            <option value="">
                                {t('CorporateStep5OpenInnovation.selectCongress')}
                            </option>
                            {congresses.map((c) => (
                                <option key={c._id} value={c._id}>
                                    {c.name}
                                </option>
                            ))}
                        </select>

                        <label style={labelStyle}>
                            {t('CorporateStep5OpenInnovation.workNumber')}
                        </label>
                        <div style={{ display: 'flex', gap: 8 }}>
                            <input
                                type="text"
                                value={impCaseId}
                                onChange={(e) => setImpCaseId(e.target.value)}
                                style={{ ...inputStyle, flex: 1 }}
                            />
                            <button
                                type="button"
                                onClick={runSearch}
                                disabled={!impCongress || !impCaseId.trim() || searching}
                                style={{
                                    padding: '8px 16px',
                                    background:
                                        !impCongress || !impCaseId.trim() || searching ? '#cfd5dc' : '#4f46e5',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: 6,
                                    cursor: 'pointer',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {searching
                                    ? '...'
                                    : t('CorporateStep5OpenInnovation.search')}
                            </button>
                        </div>

                        {searchDone && preview && preview.found && (
                            <div
                                style={{
                                    background: '#eef2ff',
                                    border: '1px solid #c7d2fe',
                                    borderRadius: 8,
                                    padding: '12px 14px',
                                    marginTop: 14,
                                }}
                            >
                                <div style={{ fontSize: 11, color: '#4f46e5', fontWeight: 600, textTransform: 'uppercase' }}>
                                    {t('CorporateStep5OpenInnovation.importFound')}
                                </div>
                                <div style={{ fontWeight: 600, marginTop: 4 }}>{preview.title}</div>
                                {preview.abstractUrl && (
                                    <a
                                        href={preview.abstractUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{ fontSize: 12, color: '#4f46e5', marginTop: 6, display: 'inline-block' }}
                                    >
                                        ↗ {t('CorporateStep5OpenInnovation.viewPdf')}
                                    </a>
                                )}
                                <p style={{ fontSize: 12, color: '#777', marginTop: 10 }}>
                                    {t('CorporateStep5OpenInnovation.importPendingNote')}
                                </p>
                                <button
                                    type="button"
                                    onClick={continueFromImport}
                                    style={{
                                        padding: '8px 16px',
                                        background: '#4f46e5',
                                        color: '#fff',
                                        border: 'none',
                                        borderRadius: 6,
                                        cursor: 'pointer',
                                        marginTop: 10,
                                    }}
                                >
                                    {t('CorporateStep5OpenInnovation.continueFilling')}
                                </button>
                            </div>
                        )}

                        {searchDone && (!preview || !preview.found) && (
                            <p style={{ color: '#dc2626', fontSize: '0.9em', marginTop: 12 }}>
                                {t('CorporateStep5OpenInnovation.importNotFound')}
                            </p>
                        )}
                    </div>
                )}
            </div>
        </Modal.Modal>
    );
}

// ---------------------------------------------------------------------------
//  Section: lists the company's Open Innovation stories + add/edit/delete
// ---------------------------------------------------------------------------
export function OpenInnovationSection({ companyId, ranking, companyName, t }) {
    const api = useApi();

    const [tracks, setTracks] = useState([]);
    const [stories, setStories] = useState([]);
    const [modalOpen, setModalOpen] = useState(false);
    const [tracksOpen, setTracksOpen] = useState(false);
    const [editing, setEditing] = useState(null);

    const loadStories = useCallback(async () => {
        if (!companyId) return;
        try {
            const res = await api.get(
                `/companies/${companyId}/ranking-cases?ranking=${ranking}`
            );
            if (Array.isArray(res)) setStories(res);
        } catch (e) {
            /* silent */
        }
    }, [companyId, ranking]);

    useEffect(() => {
        api.get('/open-innovation-tracks')
            .then((res) => {
                if (Array.isArray(res)) setTracks(res);
            })
            .catch(() => {});
        loadStories();
    }, [loadStories]);

    const openCreate = () => {
        setEditing(null);
        setModalOpen(true);
    };
    const openEdit = (story) => {
        setEditing(story);
        setModalOpen(true);
    };
    const removeStory = async (story) => {
        // eslint-disable-next-line no-alert
        if (!window.confirm(t('CorporateStep5OpenInnovation.confirmDelete'))) return;
        try {
            await api.delete(`/companies/${companyId}/ranking-cases/${story._id}`);
            loadStories();
        } catch (e) {
            /* silent */
        }
    };
    const onSaved = () => {
        setModalOpen(false);
        setEditing(null);
        loadStories();
    };

    const trackName = (id) => {
        const tr = tracks.find((x) => x.id === id);
        return tr ? `${tr.id} · ${tr.title}` : `${t('CorporateStep5OpenInnovation.trackPrefix')} ${id}`;
    };

    return (
        <div style={{ marginBottom: 28 }}>
            <h3 style={{ fontSize: '1.05em', fontWeight: 600, marginBottom: 4 }}>
                {t('CorporateStep5OpenInnovation.sectionTitle')}
            </h3>
            <p style={{ fontSize: '0.9em', color: '#666', marginBottom: 12 }}>
                {t('CorporateStep5OpenInnovation.sectionDescr')}
            </p>

            <button
                type="button"
                onClick={openCreate}
                style={{
                    padding: '10px 18px',
                    background: '#4f46e5',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 6,
                    cursor: 'pointer',
                    fontWeight: 500,
                }}
            >
                + {t('CorporateStep5OpenInnovation.addCase')}
            </button>

            {stories.length === 0 && (
                <p style={{ fontSize: '0.85em', color: '#999', marginTop: 12 }}>
                    {t('CorporateStep5OpenInnovation.emptyState')}
                </p>
            )}

            <div style={{ marginTop: 12 }}>
                {stories.map((s) => {
                    const isDraft = s.status === 'draft';
                    return (
                        <div
                            key={s._id}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                padding: '10px 12px',
                                border: '1px solid #eee',
                                borderRadius: 8,
                                marginBottom: 8,
                            }}
                        >
                            <div style={{ minWidth: 0 }}>
                                <div style={{ fontWeight: 500 }}>{s.title}</div>
                                <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>
                                    {trackName(s.track)}
                                </div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                                <StatusBadge status={s.status} t={t} />
                                {isDraft && (
                                    <>
                                        <button
                                            type="button"
                                            onClick={() => openEdit(s)}
                                            style={{
                                                border: '1px solid #ddd',
                                                background: '#fff',
                                                borderRadius: 6,
                                                cursor: 'pointer',
                                                padding: '4px 8px',
                                            }}
                                            aria-label={t('glossary:edit')}
                                        >
                                            ✎
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => removeStory(s)}
                                            style={{
                                                border: '1px solid #ddd',
                                                background: '#fff',
                                                borderRadius: 6,
                                                cursor: 'pointer',
                                                padding: '4px 8px',
                                                color: '#dc2626',
                                            }}
                                            aria-label={t('glossary:delete')}
                                        >
                                            ×
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            <AddCaseModal
                isOpen={modalOpen}
                close={() => {
                    setModalOpen(false);
                    setEditing(null);
                }}
                api={api}
                companyId={companyId}
                ranking={ranking}
                companyName={companyName}
                tracks={tracks}
                editing={editing}
                onSaved={onSaved}
                onOpenTracks={() => setTracksOpen(true)}
                t={t}
            />
            <TracksModal
                isOpen={tracksOpen}
                close={() => setTracksOpen(false)}
                tracks={tracks}
                t={t}
            />
        </div>
    );
}

export default OpenInnovationSection;
